import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  CartesianGrid,
} from "recharts";
import NumberFlow from "@number-flow/react";
import { cn } from "@/utils/Functions";
import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { Widget } from "./Widget";
import {
  PackageCheck,
  ArrowUpRight,
  ArrowDownLeft,
  AlertTriangle,
  AlertCircle,
  Clock,
} from "lucide-react";
import type { DragHandleProps } from "../../constants";
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs";
import { useDeliveryManagerData } from "@/hooks/delivery-manager/useDeliveryManagerData";
import { isPayment } from "@/types/delivery-manager.types";
import type { DeliveryItem, ChartPoint, MonthTick } from "@/types/delivery-manager.types";
import { statusConfig, formatCents, shortDate } from "@/constants/delivery-manager.constants";

const TODAY_ISO = new Date().toISOString().slice(0, 10);

function buildCashFlowChart(items: DeliveryItem[]) {
  const payments = items.filter(isPayment);
  const startDate = new Date(TODAY_ISO);
  startDate.setMonth(startDate.getMonth() - 5);

  const monthToIdx: Record<string, number> = {};
  const monthTicks: MonthTick[] = [];
  for (let i = 0; i < 9; i++) {
    const d = new Date(startDate);
    d.setMonth(d.getMonth() + i);
    const key = d.toISOString().slice(0, 7);
    monthToIdx[key] = i;
    monthTicks.push({ idx: i, label: d.toLocaleString("en-US", { month: "short" }) });
  }

  function dateToX(iso: string): number | null {
    const monthKey = iso.slice(0, 7);
    const idx = monthToIdx[monthKey];
    if (idx === undefined) return null;
    const day = parseInt(iso.slice(8, 10), 10);
    const daysInMonth = new Date(
      parseInt(monthKey.slice(0, 4), 10),
      parseInt(monthKey.slice(5, 7), 10),
      0,
    ).getDate();
    return idx + day / daysInMonth;
  }

  const sorted = [...payments].sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  interface RawPoint {
    x: number;
    delta: number;
    itemId?: string;
    label: string;
    txn?: ChartPoint["txn"];
  }
  const rawPoints: RawPoint[] = [];
  rawPoints.push({ x: 0, delta: 0, label: monthTicks[0].label });

  for (const p of sorted) {
    const x = dateToX(p.dueDate);
    if (x === null) continue;
    const sign = p.direction === "receive" ? 1 : -1;
    rawPoints.push({
      x,
      delta: p.amountCents * sign,
      itemId: p.id,
      label: `${p.title} (${shortDate(p.dueDate)})`,
      txn: {
        title: p.title,
        amountCents: p.amountCents,
        direction: p.direction,
        dueDate: p.dueDate,
      },
    });
  }

  rawPoints.push({ x: 8, delta: 0, label: monthTicks[8].label });
  rawPoints.sort((a, b) => a.x - b.x);

  const todayMonth = TODAY_ISO.slice(0, 7);
  const todayX = dateToX(TODAY_ISO) ?? monthToIdx[todayMonth] ?? 4;

  let cumulative = 0;
  const points: ChartPoint[] = rawPoints.map((rp) => {
    cumulative += rp.delta;
    const isPast = rp.x <= todayX;
    return {
      monthIdx: rp.x,
      actual: isPast ? cumulative : null,
      projected: isPast && rp.x === todayX ? cumulative : !isPast ? cumulative : null,
      itemId: rp.itemId,
      label: rp.label,
      txn: rp.txn,
    };
  });

  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i].actual !== null) {
      points[i].projected = points[i].actual;
      break;
    }
  }

  return { points, monthTicks, todayX };
}

function ChartTooltipContent({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartPoint }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  const balance = point.actual ?? point.projected ?? 0;

  return (
    <div className="rounded-lg border border-border/70 bg-card/70 px-2.5 py-1.5 shadow-lg backdrop-blur-sm max-w-[200px]">
      {point.txn ? (
        <>
          <p className="text-[10px] font-medium text-foreground truncate">{point.txn.title}</p>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="text-[9px] text-muted-foreground">{shortDate(point.txn.dueDate)}</span>
            <span
              className={cn(
                "text-[10px] font-semibold",
                point.txn.direction === "receive" ? "text-emerald-400" : "text-red-400",
              )}
            >
              {point.txn.direction === "receive" ? "+" : "-"}
              {formatCents(point.txn.amountCents)}
            </span>
          </div>
          <div className="border-t border-border/50 mt-1 pt-1">
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-muted-foreground">Balance</span>
              <span className="text-[10px] font-semibold text-foreground">{formatCents(balance)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9px] text-muted-foreground">{point.label}</span>
          <span className="text-[10px] font-semibold text-foreground">{formatCents(balance)}</span>
        </div>
      )}
    </div>
  );
}

const UpcomingCard = ({
  item,
  navigate,
  isDemo,
}: {
  item: DeliveryItem;
  navigate: (p: string) => void;
  isDemo: boolean;
}) => {
  const payment = isPayment(item);
  const isSend = item.direction === "send";
  const StatusIcon = statusConfig[item.status].icon;
  const counterparty = isSend ? item.to : item.from;
  const cfg = statusConfig[item.status];

  return (
    <button
      onClick={() =>
        navigate(isDemo ? PATH_ROOT.demo.deliveryManager : PATH_ROOT.main.deliveryManager)
      }
      className="relative rounded-2xl bg-muted/40 hover:bg-muted/60 transition-colors p-2.5 flex flex-col gap-1 text-left cursor-pointer overflow-hidden"
    >
      <div className="size-8 rounded-lg bg-neutral-800 dark:bg-neutral-50/10 grid place-items-center shrink-0 border border-border/40">
        <StatusIcon className="size-4 text-foreground" strokeWidth={1.5} />
      </div>

      <div className="flex-1 py-3" />

      <div
        className={cn(
          "inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full w-fit mb-0.5",
          cfg.className,
        )}
      >
        {shortDate(item.dueDate)}
      </div>

      <p
        className={cn(
          "text-sm font-medium tabular-nums leading-tight truncate",
          payment ? (isSend ? "text-red-400" : "text-emerald-400") : "text-foreground",
        )}
      >
        {payment ? (
          <>
            {isSend ? "-" : "+"}
            {formatCents(item.amountCents)}
          </>
        ) : (
          item.title
        )}
      </p>

      <p className="text-[10px] text-muted-foreground truncate pr-6">
        {counterparty.name}
      </p>

      <div className="absolute bottom-2 right-2 size-6 rounded-full bg-background/90 grid place-items-center border border-border/40">
        <ArrowUpRight className="size-3 text-muted-foreground" />
      </div>
    </button>
  );
};

const FeedItem = ({
  item,
  navigate,
  isDemo,
}: {
  item: DeliveryItem;
  navigate: (p: string) => void;
  isDemo: boolean;
}) => {
  const payment = isPayment(item);
  const isSend = item.direction === "send";
  const StatusIcon = statusConfig[item.status].icon;
  const counterparty = isSend ? item.to : item.from;

  return (
    <button
      onClick={() =>
        navigate(isDemo ? PATH_ROOT.demo.deliveryManager : PATH_ROOT.main.deliveryManager)
      }
      className={cn(
        "flex items-start gap-2.5 px-2.5 py-2 cursor-pointer transition-colors text-left w-full hover:bg-muted/30 rounded-xl bg-muted/0",
        // isSend ? "border-l-red-400/50" : "border-l-emerald-400/50",
      )}
    >
      <div className="relative shrink-0 mt-0.5">
        <div className="size-7 rounded-full bg-muted grid place-items-center text-[10px] font-medium text-muted-foreground">
          {counterparty.initials}
        </div>
        <div
          className={cn(
            "absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full flex items-center justify-center border-[1.5px] border-background",
            isSend ? "bg-red-400/20" : "bg-emerald-400/20",
          )}
        >
          {isSend ? (
            <ArrowUpRight className="size-2 text-red-400" />
          ) : (
            <ArrowDownLeft className="size-2 text-emerald-400" />
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1.5">
          <span className="text-xs font-medium text-foreground truncate">{counterparty.name}</span>
          {payment && (
            <span
              className={cn(
                "text-xs font-semibold shrink-0 tabular-nums",
                isSend ? "text-red-400" : "text-emerald-400",
              )}
            >
              {isSend ? "-" : "+"}
              {formatCents(item.amountCents)}
            </span>
          )}
        </div>
        <p className="text-[11px] text-foreground/80 truncate">{item.title}</p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[9px] text-muted-foreground/60">{shortDate(item.dueDate)}</span>
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0.5 rounded-full",
              statusConfig[item.status].className,
            )}
          >
            <StatusIcon className="size-2" />
            {statusConfig[item.status].label}
          </span>
        </div>
      </div>
    </button>
  );
};

export const DeliveryManagerWidget = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => {
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const { items: deliveryItems } = useDeliveryManagerData();
  const items = deliveryItems;

  const completedItems = useMemo(
    () => items.filter((i: DeliveryItem) => i.status === "completed"),
    [items],
  );
  const futureItems = useMemo(
    () =>
      items
        .filter((i: DeliveryItem) => i.status !== "completed")
        .sort((a: DeliveryItem, b: DeliveryItem) => {
          const order = ["overdue", "in_progress", "pending", "upcoming"];
          return order.indexOf(a.status) - order.indexOf(b.status);
        }),
    [items],
  );

  const chartData = useMemo(() => buildCashFlowChart(items), [items]);

  const stats = useMemo(() => {
    let sendTotal = 0;
    let receiveTotal = 0;
    let overdueCount = 0;
    let upcomingOutboundCents = 0;
    let nextDue: DeliveryItem | null = null;

    for (const item of items) {
      if (isPayment(item)) {
        if (item.direction === "send") sendTotal += item.amountCents;
        else receiveTotal += item.amountCents;
      }
      if (item.status === "overdue") overdueCount++;
      if (item.status !== "completed" && item.direction === "send" && isPayment(item)) {
        upcomingOutboundCents += item.amountCents;
      }
      if (item.status !== "completed" && (!nextDue || item.dueDate < nextDue.dueDate)) {
        nextDue = item;
      }
    }

    return { sendTotal, receiveTotal, overdueCount, upcomingOutboundCents, nextDue };
  }, [items]);

  const { points, monthTicks, todayX } = chartData;

  return (
    <Widget
      title="Delivery Manager"
      icon={<PackageCheck className="size-5" aria-hidden="true" />}
      linkTo={isDemo ? PATH_ROOT.demo.deliveryManager : PATH_ROOT.main.deliveryManager}
      dragHandleProps={dragHandleProps}
    >
      <Tabs defaultValue="graph" className="flex flex-col justify-between gap-2 h-full">
        

        <TabsPanel value="graph">
          <div className="flex flex-col gap-2">
            

            <div className="flex items-center gap-2">
              <NumberFlow
                value={stats.upcomingOutboundCents / 100}
                format={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }}
                className="text-3xl md:text-4xl font-semibold tabular-nums leading-tight truncate tracking-[0.02em]"
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <p className="">Outbound</p>
                <p className="font-medium text-muted-foreground truncate">Next Due: {stats.nextDue ? shortDate(stats.nextDue.dueDate) : "—"}</p>
              </div>
            </div>

            {/* Chart */}
            <div className="h-[130px] -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={points} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="widgetActualGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--gradient-blue-purple-to)" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="var(--gradient-amber-red-from)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="widgetProjectedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--gradient-blue-purple-to)" stopOpacity={0.12} />
                      <stop offset="100%" stopColor="var(--gradient-amber-red-from)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.08} />
                  <XAxis
                    dataKey="monthIdx"
                    type="number"
                    domain={[0, 8]}
                    axisLine={false}
                    tickLine={false}
                    ticks={monthTicks.map((t) => t.idx)}
                    tickFormatter={(idx: number) => monthTicks.find((t) => t.idx === idx)?.label ?? ""}
                    tick={{ fontSize: 9, fill: "#71717a" }}
                    allowDuplicatedCategory={false}
                  />
                  <YAxis hide domain={["dataMin - 100000", "dataMax + 200000"]} />
                  <RechartsTooltip content={<ChartTooltipContent />} />
                  <ReferenceLine
                    x={todayX}
                    stroke="rgb(113, 113, 122)"
                    strokeDasharray="3 3"
                    strokeOpacity={0.4}
                    label={{ value: "Today", position: "insideTopRight", fontSize: 8, fill: "#71717a" }}
                  />
                  <Area
                    dataKey="actual"
                    type="linear"
                    stroke="var(--gradient-blue-purple-to)"
                    strokeWidth={1.5}
                    fill="url(#widgetActualGrad)"
                    connectNulls={false}
                    dot={(dotProps: { cx: number; cy: number; index: number }) => {
                      const pt = points[dotProps.index];
                      if (!pt?.itemId || pt.actual === null) return <circle key={dotProps.index} r={0} />;
                      const color =
                        pt.txn?.direction === "receive" ? "var(--gradient-amber-red-from)" : "var(--gradient-blue-purple-to)";
                      return (
                        <circle key={pt.itemId} cx={dotProps.cx} cy={dotProps.cy} r={3} fill={color} stroke={color} strokeWidth={1} />
                      );
                    }}
                    activeDot={false}
                  />
                  <Area
                    dataKey="projected"
                    type="linear"
                    stroke="var(--gradient-blue-purple-to)"
                    strokeWidth={1.5}
                    strokeDasharray="6 4"
                    strokeOpacity={0.5}
                    fill="url(#widgetProjectedGrad)"
                    connectNulls={false}
                    dot={(dotProps: { cx: number; cy: number; index: number }) => {
                      const pt = points[dotProps.index];
                      if (!pt?.itemId || pt.actual !== null) return <circle key={dotProps.index} r={0} />;
                      const color =
                        pt.txn?.direction === "receive" ? "var(--gradient-amber-red-from)" : "var(--gradient-blue-purple-to)";
                      return (
                        <circle key={pt.itemId} cx={dotProps.cx} cy={dotProps.cy} r={3} fill={color} stroke={color} strokeWidth={1} />
                      );
                    }}
                    activeDot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex items-center justify-between text-[10px]">
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1 text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-full">
                  <span className="size-1.5 rounded-full" style={{ backgroundColor: "var(--gradient-blue-purple-to)" }} />
                  <span className="text-muted-foreground">Actual</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-full">
                  <span
                    aria-hidden
                    className="inline-block w-3.5 shrink-0 border-b-2 border-dashed opacity-50"
                    style={{ borderBottomColor: "var(--gradient-blue-purple-to)" }}
                  />
                  <span className="text-muted-foreground">Projected</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">Out: {formatCents(stats.sendTotal)}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-medium">In: {formatCents(stats.receiveTotal)}</span>
              </div>
            </div>
          </div>
        </TabsPanel>

        <TabsPanel value="upcoming">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-[10px] text-muted-foreground">{futureItems.length} upcoming</span>
              {stats.overdueCount > 0 && (
                <span className="text-[10px] text-red-400 font-medium">{stats.overdueCount} overdue</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {futureItems.slice(0, 4).map((item) => (
                <UpcomingCard key={item.id} item={item} navigate={navigate} isDemo={isDemo} />
              ))}
            </div>
            {futureItems.length > 4 && (
              <button
                onClick={() =>
                  navigate(isDemo ? PATH_ROOT.demo.deliveryManager : PATH_ROOT.main.deliveryManager)
                }
                className="text-[10px] text-muted-foreground hover:text-foreground transition-colors text-center py-1 cursor-pointer"
              >
                +{futureItems.length - 4} more
              </button>
            )}
          </div>
        </TabsPanel>

        <TabsPanel value="completed">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between px-1 pb-1 border-b border-border/40">
              <span className="text-[10px] text-muted-foreground">{completedItems.length} completed</span>
            </div>
            {completedItems.slice(0, 3).map((item) => (
              <FeedItem key={item.id} item={item} navigate={navigate} isDemo={isDemo} />
            ))}
            {completedItems.length > 4 && (
              <button
                onClick={() =>
                  navigate(isDemo ? PATH_ROOT.demo.deliveryManager : PATH_ROOT.main.deliveryManager)
                }
                className="text-[10px] text-muted-foreground hover:text-foreground transition-colors text-center py-1 cursor-pointer"
              >
                +{completedItems.length - 4} more
              </button>
            )}
          </div>
        </TabsPanel>

        <TabsList className="w-full" indicatorColor="secondary" variant="pill">
          <TabsTab value="graph" className="flex-1 text-[11px]">Cash Flow</TabsTab>
          <TabsTab value="upcoming" className="flex-1 text-[11px]">
            Upcoming
          </TabsTab>
          <TabsTab value="completed" className="flex-1 text-[11px]">Completed</TabsTab>
        </TabsList>
      </Tabs>
    </Widget>
  );
};
